import { queryGetCard, queryGetCardWithoutRequestTracing } from './get-card';

export interface GetCardQueryVariables {
	viewId?: string | null;
	cardId: string;
	pageId?: string | null;
	accountId?: string | null;
}

export type GetCardGraphQLResponse = {
	data?: { getItem?: { card?: unknown } };
	errors?: Array<{ message?: string }>;
};

function graphQLErrorMessages(value: unknown): string[] {
	if (!value || typeof value !== 'object') {
		return [];
	}
	const errors = (value as { errors?: unknown }).errors;
	if (!Array.isArray(errors)) {
		return [];
	}
	return errors
		.map(entry => (entry as { message?: string })?.message ?? '')
		.filter(Boolean);
}

function hasUnknownTracingArgument(
	messages: readonly string[],
	fieldPath: 'getItem/card' | 'getItem/cardData'
): boolean {
	return messages.some(
		message =>
			message.includes(
				`Unknown field argument requestId @ '${fieldPath}'`
			) ||
			message.includes(
				`Unknown field argument requester @ '${fieldPath}'`
			)
	);
}

/** True when the API schema has no `requestId` / `requester` on `getItem.card`. */
export function isGetCardUnknownTracingArgumentError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : String(error);
	return hasUnknownTracingArgument(
		[message, ...graphQLErrorMessages(error)],
		'getItem/card'
	);
}

function isNotFoundGraphQLErrorEntry(err: {
	errorType?: string;
	message?: string;
}): boolean {
	const type = err?.errorType?.trim();
	if (type === 'NOT_FOUND') {
		return true;
	}
	const message = err?.message?.trim().toLowerCase() ?? '';
	return message === 'card not found' || message.includes('card not found');
}

/**
 * True when getItem.card failed because the card does not exist.
 * Accepts either a GraphQL response (`errors` + null card) or a thrown
 * {@link Error} from JepRequestService.query (`cause.graphQLErrors` / message).
 */
export function isGetCardNotFoundError(responseOrError: unknown): boolean {
	if (!responseOrError || typeof responseOrError !== 'object') {
		return false;
	}

	// Thrown by JepRequestService.throwFromGraphQLErrors
	if (responseOrError instanceof Error) {
		const cause = (responseOrError as Error & { cause?: unknown }).cause;
		if (cause && typeof cause === 'object') {
			const graphQLErrors = (cause as { graphQLErrors?: unknown })
				.graphQLErrors;
			if (Array.isArray(graphQLErrors)) {
				return graphQLErrors.some(entry =>
					isNotFoundGraphQLErrorEntry(
						(entry ?? {}) as {
							errorType?: string;
							message?: string;
						}
					)
				);
			}
		}
		const topLevelErrors = (responseOrError as { errors?: unknown }).errors;
		if (Array.isArray(topLevelErrors)) {
			return topLevelErrors.some(entry =>
				isNotFoundGraphQLErrorEntry(
					(entry ?? {}) as { errorType?: string; message?: string }
				)
			);
		}
		return responseOrError.message.toLowerCase().includes('card not found');
	}

	const record = responseOrError as {
		data?: { getItem?: { card?: unknown } };
		errors?: Array<{
			errorType?: string;
			message?: string;
			path?: unknown;
		}>;
	};
	const card = record.data?.getItem?.card;
	if (card != null) {
		return false;
	}
	const errors = Array.isArray(record.errors) ? record.errors : [];
	return errors.some(err => isNotFoundGraphQLErrorEntry(err ?? {}));
}

async function runGetCardWithoutTracing(
	runQuery: (
		query: string,
		variables: Record<string, unknown>
	) => Promise<unknown>,
	baseVars: Record<string, unknown>
): Promise<GetCardGraphQLResponse> {
	return (await runQuery(
		queryGetCardWithoutRequestTracing,
		baseVars
	)) as GetCardGraphQLResponse;
}

/**
 * Loads `getItem.card`, retrying without tracing args when the schema is older.
 */
export async function fetchGetCardItem(
	runQuery: (
		query: string,
		variables: Record<string, unknown>
	) => Promise<unknown>,
	variables: GetCardQueryVariables,
	tracing: { requestId: string; requester: string }
): Promise<GetCardGraphQLResponse> {
	const baseVars: Record<string, unknown> = {
		viewId: variables.viewId ?? null,
		cardId: variables.cardId,
		pageId: variables.pageId ?? null,
	};
	const accountId = variables.accountId?.trim();
	if (accountId) {
		baseVars.accountId = accountId;
	}

	try {
		const traced = (await runQuery(queryGetCard, {
			...baseVars,
			requestId: tracing.requestId,
			requester: tracing.requester,
		})) as GetCardGraphQLResponse;
		if (
			hasUnknownTracingArgument(
				graphQLErrorMessages(traced),
				'getItem/card'
			)
		) {
			return runGetCardWithoutTracing(runQuery, baseVars);
		}
		return traced;
	} catch (error) {
		if (!isGetCardUnknownTracingArgumentError(error)) {
			throw error;
		}
		return runGetCardWithoutTracing(runQuery, baseVars);
	}
}
